import React, { useEffect, useRef, useState, useCallback } from 'react';
import styles from './Products.module.css';
import { FaBath, FaHome, FaShower } from 'react-icons/fa';
import { MdOutlineKitchen, MdDeck, MdOutlineApps } from 'react-icons/md';
import { GiHotSurface, GiFireplace } from 'react-icons/gi';
import { BsBricks } from 'react-icons/bs';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
// import { digitalOceanSpacesService } from '../../services/digitalOceanSpacesService';
import { Image } from '../components/ImagePreviewModal';

interface Product {
  name: string;
  icon: React.ReactNode;
}

const products: Product[] = [
  { name: 'Bathrooms', icon: <FaBath /> },
  { name: 'Kitchens', icon: <MdOutlineKitchen /> },
  { name: 'Floors', icon: <MdOutlineApps /> },
  { name: 'Fireplace Surrounds', icon: <GiFireplace /> },
  { name: 'Backsplashes', icon: <BsBricks /> },
  { name: 'Entryways / Mudrooms', icon: <FaHome /> },
  { name: 'Heated Floors', icon: <GiHotSurface /> },
  { name: 'Custom Showers', icon: <FaShower /> },
  { name: 'Patio Decks', icon: <MdDeck /> },
];

const Products: React.FC = () => {
  const [visibleElements, setVisibleElements] = useState<number[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [images] = useState<Image[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [loading] = useState<boolean>(false);
  const scrollPositionRef = useRef<number>(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Staggered animation for title + 9 product cards
            const totalElements = 1 + products.length; // title + cards
            for (let i = 0; i < totalElements; i++) {
              setTimeout(() => {
                setVisibleElements(prev => {
                  if (!prev.includes(i)) {
                    return [...prev, i];
                  }
                  return prev;
                });
              }, i * 100); // 100ms delay between each element
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Fetch images from Digital Ocean - currently disabled
  // const fetchImages = useCallback(async () => {
  //   try {
  //     setLoading(true);
  //     const response = await digitalOceanSpacesService.listGalleryFiles(20);

  //     if (response.success && response.files) {
  //       setImages(response.files);
  //     }
  //   } catch (err) {
  //     console.error('Error fetching images:', err);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  // Handle card click - currently disabled
  // const handleCardClick = (product: Product): void => {
  //   setSelectedProduct(product);
  //   setCurrentImageIndex(0);
  //   setModalOpen(true);
  //   if (images.length === 0) {
  //     fetchImages();
  //   }
  //   // Save current scroll position
  //   scrollPositionRef.current = window.scrollY;
  //   // Prevent body scroll
  //   document.body.style.overflow = 'hidden';
  //   document.body.style.position = 'fixed';
  //   document.body.style.top = `-${scrollPositionRef.current}px`;
  //   document.body.style.width = '100%';
  // };

  // Handle modal close
  const handleCloseModal = (): void => {
    setModalOpen(false);
    setSelectedProduct(null);
    setCurrentImageIndex(0);
    // Restore body scroll
    const scrollY = scrollPositionRef.current;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    // Restore scroll position
    window.scrollTo(0, scrollY);
  };

  // Navigate carousel
  const handlePrevious = useCallback((): void => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const handleNext = useCallback((): void => {
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!modalOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseModal();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [modalOpen, handlePrevious, handleNext]);

  return (
    <section ref={sectionRef} className={styles.productsSection}>
      <h1 className={`${styles.title} ${visibleElements.includes(0) ? styles.fadeInDown : styles.animationHidden}`}>
        Our Products & Services
      </h1>
      <div className={styles.grid}>
        {products.map((product, index) => (
          <div
            key={index}
            className={`${styles.card} ${visibleElements.includes(index + 1) ? styles.scaleIn : styles.animationHidden}`}
            // onClick={() => handleCardClick(product)}
            // style={{ cursor: 'pointer' }}
          >
            <div className={styles.icon}>{product.icon}</div>
            <h3>{product.name}</h3>
          </div>
        ))}
      </div>

      {/* Product Carousel Modal */}
      {modalOpen && selectedProduct && (
        <div className={styles.modal} onClick={handleCloseModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className={styles.modalHeader}>
              <h2>{selectedProduct.name}</h2>
              <button
                className={styles.closeButton}
                onClick={handleCloseModal}
                aria-label="Close modal"
                type="button"
              >
                <X size={28} />
              </button>
            </div>

            {/* Carousel */}
            {loading ? (
              <div className={styles.modalLoading}>
                <p>Loading images...</p>
              </div>
            ) : images.length === 0 ? (
              <div className={styles.modalEmpty}>
                <p>No images available</p>
              </div>
            ) : (
              <div className={styles.carouselContainer}>
                {/* Previous Button */}
                <button
                  className={`${styles.carouselButton} ${styles.prevButton}`}
                  onClick={handlePrevious}
                  aria-label="Previous image"
                  type="button"
                >
                  <ChevronLeft size={32} />
                </button>

                {/* Image Display */}
                <div className={styles.imageContainer}>
                  <img
                    src={images[currentImageIndex].path}
                    alt={images[currentImageIndex].name}
                    className={styles.carouselImage}
                  />
                </div>

                {/* Next Button */}
                <button
                  className={`${styles.carouselButton} ${styles.nextButton}`}
                  onClick={handleNext}
                  aria-label="Next image"
                  type="button"
                >
                  <ChevronRight size={32} />
                </button>
              </div>
            )}

            {/* Image Counter */}
            {images.length > 0 && (
              <div className={styles.imageCounter}>
                {currentImageIndex + 1} / {images.length}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Products;